import { useState } from 'react'
import {
  ButtonBase,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { PrintOutlined as PrintIcon } from '@mui/icons-material'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { useToast } from '@/shared/contexts/ToastContext'
import { RADIUS } from '@/shared/styles/radii'
import { scaledFontSize } from '@/shared/styles/typography'

import { type HomecareRecipient } from './homecareApi'
import { addresseeLines } from './printHomeVisitReport'

const KIND_LABEL: Record<HomecareRecipient['kind'], string> = {
  physician: '処方医',
  care_manager: 'ケアマネ',
  other: 'その他',
}

interface Props {
  recipients: HomecareRecipient[]
  // アイコンの読み上げ名 (例: 訪問報告書を印刷)
  ariaLabel: string
  // 選んだ宛先 (null = 宛名なし) で刷る。別窓を開けなければ false
  print: (targets: (HomecareRecipient | null)[]) => boolean
}

// 在宅の紙 (訪問報告書・計画書) を出す口。アイコン 1 つ → popover に宛先の行 (先頭は全員分)。
// 行を押すとその宛先で刷る。確認・プレビュー・「出力済み」は持たない (栄養の「出す」と同じ形。
// design-book 04-apps/kakeru.md 管理栄養士の報告書)。宛先が無い患者は宛名なしの 1 枚。
export const RecipientPrintButton = ({
  recipients,
  ariaLabel,
  print,
}: Props) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const { showToast } = useToast()

  const printTo = (targets: (HomecareRecipient | null)[]) => {
    if (!print(targets)) {
      showToast('印刷の画面を開けませんでした', 'error')
      return
    }
    setAnchor(null)
  }

  const rows: {
    key: string
    label: string
    detail: string
    targets: (HomecareRecipient | null)[]
  }[] =
    recipients.length === 0
      ? [{ key: 'none', label: '宛名なし', detail: '', targets: [null] }]
      : [
          ...(recipients.length > 1
            ? [
                {
                  key: 'all',
                  label: `全員分 (${recipients.length} 枚)`,
                  detail: '',
                  targets: recipients,
                },
              ]
            : []),
          ...recipients.map((recipient, index) => ({
            key: String(index),
            label: KIND_LABEL[recipient.kind],
            detail: addresseeLines(recipient).join(' '),
            targets: [recipient],
          })),
        ]

  return (
    <>
      <Tooltip title="印刷">
        <IconButton
          size="small"
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label={ariaLabel}
          sx={{ color: colorTokens.fg.muted }}
        >
          <PrintIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={anchor != null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 0.5,
              width: 360,
              borderRadius: `${RADIUS.lg}px`,
              p: 0.5,
              bgcolor: colorTokens.bg.default,
              border: `1px solid ${colorTokens.border.subtle}`,
              boxShadow: colorTokens.shadow.popover,
            },
          },
        }}
      >
        <Stack>
          {rows.map((row) => (
            <ButtonBase
              key={row.key}
              onClick={() => printTo(row.targets)}
              aria-label={`${row.label} ${row.detail}を印刷`}
              sx={{
                justifyContent: 'flex-start',
                gap: 1,
                minHeight: 32,
                px: 1,
                borderRadius: `${RADIUS.md}px`,
                '&:hover, &.Mui-focusVisible': {
                  bgcolor: colorTokens.bg.muted,
                },
              }}
            >
              <Typography
                component="span"
                sx={{
                  flexShrink: 0,
                  fontSize: scaledFontSize('bodySm'),
                  color: colorTokens.fg.default,
                }}
              >
                {row.label}
              </Typography>
              <Typography
                component="span"
                noWrap
                sx={{
                  ml: 'auto',
                  minWidth: 0,
                  fontSize: scaledFontSize('label'),
                  color: colorTokens.fg.muted,
                }}
              >
                {row.detail}
              </Typography>
            </ButtonBase>
          ))}
        </Stack>
      </Popover>
    </>
  )
}
